import React, { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import Modal, { ModalBody, ModalFooter } from '../Modal';
import { Flex, Text } from 'rebass';
import Button from '../Button/Button';
import { Label } from '../Form';
import TagPicker from './TagPicker';
import useNotify from '../../hooks/useNotify';
import useMode from '../../hooks/useMode';
import { gql } from '@/modes/waterSupplier/gql';
import { gql as company_gql } from '@/modes/company/gql';
import { IdArrayInput } from '../../hooks/useTablePages';

type BulkTagModalProps = {
  onClose: () => void;
  selectedIds: IdArrayInput;
  objectType: 'assembly' | 'waterService' | 'customer';
};

const ADD_TAGS_TO_ASSEMBLIES = gql(`
  mutation AddTagsToAssemblies($assemblyIds: [ID!]!, $tags: [String!]!) {
    addTagsToAssemblies(assemblyIds: $assemblyIds, tags: $tags) {
      id
      tags
    }
  }
`);

const ADD_TAGS_TO_WATER_SERVICES = gql(`
  mutation AddTagsToWaterServices($waterServiceIds: [ID!]!, $tags: [String!]!) {
    addTagsToWaterServices(waterServiceIds: $waterServiceIds, tags: $tags) {
      id
      tags
    }
  }
`);

const ADD_TAGS_TO_CUSTOMERS_WATER_SUPPLIER = gql(`
  mutation AddTagsToCustomers($customerIds: [ID!]!, $tags: [String!]!) {
    addTagsToCustomers(customerIds: $customerIds, tags: $tags) {
      id
      tags
    }
  }
`);

const ADD_TAGS_TO_CUSTOMERS_COMPANY = company_gql(`
  mutation AddTagsToCustomers($customerIds: [ID!]!, $tags: [String!]!) {
    addTagsToCustomers(customerIds: $customerIds, tags: $tags) {
      id
      tags
    }
  }
`);

const BulkTagModal: React.FC<BulkTagModalProps> = ({
  onClose,
  selectedIds,
  objectType,
}) => {
  const [tags, setTags] = useState<string[]>([]);
  const { showError, showSuccess } = useNotify();
  const mode = useMode();

  const isAssembly = objectType === 'assembly';
  const isCustomer = objectType === 'customer';
  const label = isAssembly
    ? 'assembl'
    : isCustomer
      ? 'customer'
      : 'water service';
  const labelPlural =
    selectedIds.length === 1
      ? `1 ${label}${isAssembly ? 'y' : ''}`
      : `${selectedIds.length.toLocaleString()} ${label}${isAssembly ? 'ies' : 's'}`;

  const onCompleted = useCallback(() => {
    showSuccess(`Tags added to ${labelPlural}`);
    onClose();
  }, [labelPlural, onClose]);

  const onError = useCallback((error: Error) => {
    showError('Error adding tags', error);
  }, []);

  const [addTagsToAssemblies, { loading: assembliesLoading }] = useMutation(
    ADD_TAGS_TO_ASSEMBLIES,
    { onCompleted, onError }
  );

  const [addTagsToWaterServices, { loading: servicesLoading }] = useMutation(
    ADD_TAGS_TO_WATER_SERVICES,
    { onCompleted, onError }
  );

  const [addTagsToCustomers, { loading: customersLoading }] = useMutation(
    mode === 'water-supplier'
      ? ADD_TAGS_TO_CUSTOMERS_WATER_SUPPLIER
      : ADD_TAGS_TO_CUSTOMERS_COMPANY,
    { onCompleted, onError }
  );

  const loading = assembliesLoading || servicesLoading || customersLoading;

  const save = useCallback(() => {
    if (!tags.length) {
      showError('Please select at least one tag');
      return;
    }
    if (isAssembly) {
      addTagsToAssemblies({
        variables: { assemblyIds: selectedIds as string[], tags },
      });
    } else if (isCustomer) {
      addTagsToCustomers({
        variables: { customerIds: selectedIds as string[], tags },
      });
    } else {
      addTagsToWaterServices({
        variables: { waterServiceIds: selectedIds as string[], tags },
      });
    }
  }, [
    tags,
    isAssembly,
    isCustomer,
    selectedIds,
    addTagsToAssemblies,
    addTagsToCustomers,
    addTagsToWaterServices,
  ]);

  return (
    <Modal title="Add Tags" onClose={onClose}>
      <ModalBody>
        <Flex p={3} flexDirection="column" sx={{ gap: 3 }}>
          <Text variant="body1">
            Add tags to {labelPlural}. Existing tags will not be removed.
          </Text>
          {/*<Label>Tags</Label>*/}
          <TagPicker
            value={tags}
            onChange={(newTags) => setTags(newTags.slice())}
          />
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button icon="save" onClick={save} disabled={loading}>
          Add Tags
        </Button>
        <Button variant="outline" icon="slash-circle" onClick={onClose}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default BulkTagModal;
